/**
 * L1J Web Client - Login Scene
 * 로그인 화면 씬
 *
 * Gateway WebSocket 연결을 통해 인증을 수행합니다.
 * 프로토콜: { type: 'login', payload: { account, password } }
 *
 * 브라우저 새로 고침 후 자동 로그인:
 *  - 로그인 성공 시 자격증명을 localStorage 에 저장한다.
 *  - 씬이 다시 생성될 때 저장된 자격증명이 있으면 자동으로 로그인 시도한다.
 *  - 만료 시간(기본 24시간)이 지난 자격증명은 무시한다.
 */

import Phaser from 'phaser';
import { ConnectionManager } from '../../network/ConnectionManager';
import { ServerConfig } from '../../config';
import { LoginResultPayload } from '../../types/network';
import { storageManager } from '../../core/StorageManager';
import { SessionState } from '../../core/types';
import { ensureGuestCredentials, isGuestAccount, shouldUseGuestLogin } from '../../utils/guest';
import { isIntroRoute } from '../../utils/route';
import { introOverlay } from '../../ui/IntroOverlay';

/** 로그인 상태 열거 */
enum LoginStatus {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  LOGGING_IN = 'logging_in',
  AUTO_LOGGING_IN = 'auto_logging_in',
  SUCCESS = 'success',
  ERROR = 'error',
}

/** DOM 요소 IDs */
const DOM_IDS = {
  CONTAINER: 'login-form-container',
  ACCOUNT_INPUT: 'login-account',
  PASSWORD_INPUT: 'login-password',
  SUBMIT_BTN: 'login-submit',
  STATUS_TEXT: 'login-status',
  ERROR_TEXT: 'login-error',
} as const;

/** 중복 로그인 확인 모달 DOM 요소 IDs */
const DUP_MODAL_IDS = {
  MESSAGE: 'dup-login-message',
  CONFIRM_BTN: 'dup-login-confirm',
  CANCEL_BTN: 'dup-login-cancel',
} as const;

/** 중복 로그인 "확인" 이후의 강제 재로그인 타이밍 */
const FORCE_LOGIN = {
  /** 재접속 + 재로그인 전체 제한 시간 — 넘기면 로딩을 풀고 에러를 남긴다 */
  TIMEOUT_MS: 10000,
  /** 게임 서버가 기존 접속을 정리할 시간을 준 뒤 재시도하는 간격 */
  RETRY_DELAY_MS: 1200,
} as const;

export class LoginScene extends Phaser.Scene {
  private connectionManager: ConnectionManager | null = null;
  private domContainer: Phaser.GameObjects.DOMElement | null = null;
  private currentStatus: LoginStatus = LoginStatus.IDLE;
  /** 자동 로그인 시도 여부 (create() 흐름에서 한 번만 실행되도록 가드) */
  private autoLoginAttempted: boolean = false;
  /**
   * 현재 진행 중인 로그인이 자동 로그인에서 시작됐는지 추적.
   * currentStatus는 performLogin()이 LOGGING_IN으로 덮어쓰므로
   * 실패 시 저장된 자격증명 삭제 판단에는 이 필드를 사용한다.
   */
  private isAutoLoginAttempt: boolean = false;
  /** ConnectionManager에 등록한 리스너 (shutdown 시 동일 참조로 해제) */
  private connectionListeners: Array<{ event: string; handler: (data: unknown) => void }> = [];
  /** 중복 로그인 확인 다이얼로그 */
  private duplicateLoginModal: Phaser.GameObjects.DOMElement | null = null;
  private duplicateLoginBackdrop: Phaser.GameObjects.Rectangle | null = null;
  /** 확인 클릭 시 force:true로 재전송할 마지막 로그인 시도 정보 */
  private pendingCredentials: { account: string; password: string } | null = null;
  /** 중복 로그인 "확인" 이후의 강제 재로그인이 진행 중인지 여부 */
  private forceLoginPending: boolean = false;
  /** 강제 재로그인 재시도를 1회로 제한하는 플래그 (무한 루프 방지) */
  private forceLoginRetried: boolean = false;
  /** 강제 재로그인 전체 제한 시간 타이머 */
  private forceLoginTimeoutEvent: Phaser.Time.TimerEvent | null = null;
  /** 재시도 대기 타이머 */
  private forceLoginRetryEvent: Phaser.Time.TimerEvent | null = null;

  constructor() {
    super({ key: 'LoginScene' });
  }

  create(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // 씬 재진입 시(로그아웃/강제 로그아웃 후 복귀 등) 이전 상태가 남지 않도록 리셋
    this.currentStatus = LoginStatus.IDLE;
    this.autoLoginAttempted = false;
    this.isAutoLoginAttempt = false;
    this.pendingCredentials = null;
    this.forceLoginPending = false;
    this.forceLoginRetried = false;
    this.forceLoginTimeoutEvent = null;
    this.forceLoginRetryEvent = null;
    this.connectionListeners = [];

    // 다크 배경
    this.cameras.main.setBackgroundColor(0x0a0a12);

    // 배경 장식 - 골드 글로우 효과
    this.createBackgroundEffects(width, height);

    // 타이틀
    this.createTitle(width, height);

    // DOM 로그인 폼 생성
    this.createLoginForm(width, height);

    // ConnectionManager 초기화 및 연결
    this.initializeConnection();

    // Phaser는 shutdown() 메서드를 자동 호출하지 않으므로 이벤트로 정리 훅 등록
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.cleanup, this);
  }

  /**
   * 저장된 자격증명이 있으면 폼에 미리 채우고 자동 로그인 흐름을 시작한다.
   * LoginScene.create() → initializeConnection() → connect() 완료 후 호출된다.
   */
  private tryAutoLogin(): void {
    if (this.autoLoginAttempted) return;
    this.autoLoginAttempted = true;

    // 다른 곳에서 같은 계정으로 로그인해 방금 강제 로그아웃된 경우, 곧바로 자동
    // 재로그인을 시도하면 방금 접속한 세션을 다시 끊어버리는 핑퐁이 발생한다.
    if (storageManager.consumeAutoLoginSuppression()) {
      console.log('[LoginScene] 자동 로그인 건너뜀 (세션 종료 직후)');
      // 씬이 바뀌면 채팅창 안내가 사라지므로 여기서 한 번 더 알린다.
      // 자동 로그인이 걸리지 않는 이유를 사용자가 알 수 있어야 한다.
      this.updateStatus(LoginStatus.IDLE, '연결이 끊어졌습니다. 다시 로그인해 주세요.');
      return;
    }

    const stored = storageManager.loadCredentials();

    // 사람이 만든 계정 기록이 없으면 체험용 게스트로 바로 들어간다.
    // (StorageManager는 비밀번호를 저장하지 않으므로 게스트는 별도 저장소를 쓴다)
    if (shouldUseGuestLogin()) {
      this.startGuestLogin();
      return;
    }

    if (!stored) return;

    // `/login`은 자기 계정으로 들어오는 곳이다. 직전에 체험 계정으로 놀았다고 해서
    // 그 이름을 채워 두면, 자기 계정을 넣으려는 사람이 먼저 지워야 한다.
    if (isGuestAccount(stored.account)) return;

    const accountInput = this.domContainer?.getChildByID(DOM_IDS.ACCOUNT_INPUT) as HTMLInputElement | null;
    const passwordInput = this.domContainer?.getChildByID(DOM_IDS.PASSWORD_INPUT) as HTMLInputElement | null;
    const statusText = this.domContainer?.getChildByID(DOM_IDS.STATUS_TEXT) as HTMLElement | null;
    const errorText = this.domContainer?.getChildByID(DOM_IDS.ERROR_TEXT) as HTMLElement | null;

    if (!accountInput || !passwordInput || !statusText || !errorText) {
      console.warn('[LoginScene] 자동 로그인을 위한 DOM 요소를 찾을 수 없음');
      return;
    }

    // 계정명은 항상 미리 채운다
    accountInput.value = stored.account;

    // StorageManager는 보안상 비밀번호를 더 이상 localStorage에 저장하지 않는다.
    // password가 빈 값이면 자동 로그인은 건너뛰고 계정명만 채운 채 수동 입력을 유도한다.
    if (!stored.password) {
      console.log('[LoginScene] 저장된 비밀번호 없음 — 계정명만 채우고 자동 로그인 건너뜀:', stored.account);
      this.updateStatus(LoginStatus.IDLE, `다시 오신 것을 환영합니다, ${stored.account}! 비밀번호를 입력하세요.`);
      passwordInput.focus();
      return;
    }

    console.log('[LoginScene] 저장된 자격증명 발견 → 자동 로그인 시도:', stored.account);

    // 폼에 미리 채워넣기
    passwordInput.value = stored.password;

    // 자동 로그인 상태 표시
    this.updateStatus(LoginStatus.AUTO_LOGGING_IN, `다시 오신 것을 환영합니다, ${stored.account}!`);
    this.setFormEnabled(false);

    // 저장된 정보 표시
    if (stored.charName) {
      errorText.style.color = '#88ddff';
      errorText.textContent = `마지막 캐릭터: ${stored.charName}`;
    }

    // 이번 로그인 시도가 자동 로그인에서 시작됐음을 표시 (실패 시 자격증명 삭제 판단용)
    this.isAutoLoginAttempt = true;

    // 즉시 로그인 시도 (연결 상태에 따라 분기)
    this.autoLoginAttempt(stored.account, stored.password);
  }

  /**
   * 체험용 게스트 계정으로 자동 로그인한다.
   *
   * 이 브라우저에 게스트 계정이 없으면 새로 만들고(`guest_xxxxxx` + 임의 비밀번호),
   * 이미 있으면 그 계정으로 다시 들어간다 — 두 번째 방문부터 캐릭터가 유지된다.
   * 서버는 존재하지 않는 계정으로 처음 로그인할 때 계정을 만들어 주므로
   * (`AutoCreateAccounts=true`) 별도의 가입 요청이 필요 없다.
   */
  private startGuestLogin(): void {
    const guest = ensureGuestCredentials();

    const accountInput = this.domContainer?.getChildByID(DOM_IDS.ACCOUNT_INPUT) as HTMLInputElement | null;
    const passwordInput = this.domContainer?.getChildByID(DOM_IDS.PASSWORD_INPUT) as HTMLInputElement | null;

    if (accountInput) accountInput.value = guest.account;
    if (passwordInput) passwordInput.value = guest.password;

    this.updateStatus(LoginStatus.AUTO_LOGGING_IN, `체험 계정으로 접속 중… (${guest.account})`);
    this.setFormEnabled(false);

    // 실패 시 자격증명을 지울지 판단하는 데 쓰인다 (수동 로그인과 같은 경로).
    this.isAutoLoginAttempt = true;

    this.autoLoginAttempt(guest.account, guest.password);
  }

  /**
   * 자동 로그인 시도. 연결이 되어 있지 않다면 먼저 연결한다.
   */
  private autoLoginAttempt(account: string, password: string): void {
    if (!this.connectionManager) return;

    if (this.connectionManager.isConnected()) {
      console.log('[LoginScene] 자동 로그인: 이미 연결됨 — 로그인 요청 전송');
      this.performLogin(account, password);
      return;
    }

    console.log('[LoginScene] 자동 로그인: Gateway 연결 후 진행');
    this.connectionManager
      .connect()
      .then(() => {
        this.performLogin(account, password);
      })
      .catch((err: Error) => {
        console.error('[LoginScene] 자동 로그인 연결 실패:', err);
        this.isAutoLoginAttempt = false;
        this.updateStatus(LoginStatus.ERROR, '', `자동 로그인 실패: ${err.message}`);
        this.setFormEnabled(true);
      });
  }

  /**
   * 배경 장식 효과 생성
   */
  private createBackgroundEffects(width: number, height: number): void {
    // 골드 그라데이션 바 상단
    const topBar = this.add.graphics();
    topBar.fillGradientStyle(0xc9a227, 0xc9a227, 0x000000, 0x000000, 0.3, 0.3, 0, 0);
    topBar.fillRect(0, 0, width, 3);

    // 하단 장식선
    const bottomLine = this.add.graphics();
    bottomLine.lineStyle(1, 0xc9a227, 0.3);
    bottomLine.lineBetween(0, height - 80, width, height - 80);
  }

  /**
   * 타이틀 생성
   */
  private createTitle(width: number, height: number): void {
    // 메인 타이틀 - 골드 컬러
    this.add.text(width / 2, height * 0.18, 'L1J EN Classic', {
      fontSize: '52px',
      fontFamily: 'Georgia, serif',
      color: '#c9a227',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);

    // 서브타이틀
    this.add.text(width / 2, height * 0.25, 'Web Client', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    }).setOrigin(0.5);
  }

  /**
   * DOM 로그인 폼 생성
   */
  private createLoginForm(width: number, height: number): void {
    // 폼 컨테이너 생성
    const formHtml = this.createFormHtml();

    this.domContainer = this.add.dom(width / 2, height * 0.55).createFromHTML(formHtml);

    // `/` 로 들어온 사람에게는 인트로가 화면을 덮고 자동으로 월드까지 들어간다.
    // 폼은 그 아래에 그대로 두되 감춘다 — 남겨 두면 보이지도 않는 입력칸에
    // 키보드 포커스가 걸린다. 자동 로그인 코드가 이 요소들을 계속 참조하므로
    // 만들지 않는 대신 숨기는 쪽을 택했다.
    if (isIntroRoute()) {
      this.domContainer.setVisible(false);
    }

    // 폼 요소 참조
    const container = this.domContainer.getChildByID(DOM_IDS.CONTAINER) as HTMLElement;
    const accountInput = this.domContainer.getChildByID(DOM_IDS.ACCOUNT_INPUT) as HTMLInputElement;
    const passwordInput = this.domContainer.getChildByID(DOM_IDS.PASSWORD_INPUT) as HTMLInputElement;
    const submitBtn = this.domContainer.getChildByID(DOM_IDS.SUBMIT_BTN) as HTMLButtonElement;
    const statusText = this.domContainer.getChildByID(DOM_IDS.STATUS_TEXT) as HTMLElement;
    const errorText = this.domContainer.getChildByID(DOM_IDS.ERROR_TEXT) as HTMLElement;

    if (!container || !accountInput || !passwordInput || !submitBtn || !statusText || !errorText) {
      console.error('LoginScene: Failed to create DOM elements');
      return;
    }

    // Enter 키 처리
    accountInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        passwordInput.focus();
      }
    });

    passwordInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleLoginSubmit(accountInput, passwordInput, statusText, errorText);
      }
    });

    // 로그인 버튼 클릭
    submitBtn.addEventListener('click', () => {
      this.handleLoginSubmit(accountInput, passwordInput, statusText, errorText);
    });
  }

  /**
   * 로그인 폼 HTML 생성
   */
  private createFormHtml(): string {
    return `
      <div id="${DOM_IDS.CONTAINER}" style="
        background: linear-gradient(180deg, rgba(20, 20, 35, 0.95) 0%, rgba(10, 10, 18, 0.98) 100%);
        border: 2px solid #c9a227;
        border-radius: 8px;
        padding: 40px 50px;
        min-width: 360px;
        box-shadow: 0 0 30px rgba(201, 162, 39, 0.2), inset 0 0 20px rgba(0, 0, 0, 0.5);
        font-family: Arial, sans-serif;
      ">
        <!-- 계정 입력 -->
        <div style="margin-bottom: 20px;">
          <label for="${DOM_IDS.ACCOUNT_INPUT}" style="
            display: block;
            color: #c9a227;
            font-size: 14px;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 1px;
          ">Account</label>
          <input type="text" id="${DOM_IDS.ACCOUNT_INPUT}" autocomplete="username" style="
            width: 100%;
            padding: 12px 16px;
            background: rgba(0, 0, 0, 0.6);
            border: 1px solid #444444;
            border-radius: 4px;
            color: #ffffff;
            font-size: 16px;
            outline: none;
            box-sizing: border-box;
            transition: border-color 0.2s, box-shadow 0.2s;
          " onfocus="this.style.borderColor='#c9a227'; this.style.boxShadow='0 0 10px rgba(201, 162, 39, 0.3)'" onblur="this.style.borderColor='#444444'; this.style.boxShadow='none'">
        </div>

        <!-- 비밀번호 입력 -->
        <div style="margin-bottom: 25px;">
          <label for="${DOM_IDS.PASSWORD_INPUT}" style="
            display: block;
            color: #c9a227;
            font-size: 14px;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 1px;
          ">Password</label>
          <input type="password" id="${DOM_IDS.PASSWORD_INPUT}" autocomplete="current-password" style="
            width: 100%;
            padding: 12px 16px;
            background: rgba(0, 0, 0, 0.6);
            border: 1px solid #444444;
            border-radius: 4px;
            color: #ffffff;
            font-size: 16px;
            outline: none;
            box-sizing: border-box;
            transition: border-color 0.2s, box-shadow 0.2s;
          " onfocus="this.style.borderColor='#c9a227'; this.style.boxShadow='0 0 10px rgba(201, 162, 39, 0.3)'" onblur="this.style.borderColor='#444444'; this.style.boxShadow='none'">
        </div>

        <!-- 로그인 버튼 -->
        <button id="${DOM_IDS.SUBMIT_BTN}" type="button" style="
          width: 100%;
          padding: 14px;
          background: linear-gradient(180deg, #c9a227 0%, #8b7019 100%);
          border: none;
          border-radius: 4px;
          color: #000000;
          font-size: 18px;
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 2px;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 15px rgba(201, 162, 39, 0.3);
        " onmouseover="this.style.background='linear-gradient(180deg, #d4ad2d 0%, #9a7d1e 100%)'; this.style.boxShadow='0 6px 20px rgba(201, 162, 39, 0.5)'" onmouseout="this.style.background='linear-gradient(180deg, #c9a227 0%, #8b7019 100%)'; this.style.boxShadow='0 4px 15px rgba(201, 162, 39, 0.3)'">Login</button>

        <!-- 상태 텍스트 -->
        <div id="${DOM_IDS.STATUS_TEXT}" style="
          margin-top: 20px;
          text-align: center;
          color: #888888;
          font-size: 14px;
          min-height: 20px;
        "></div>

        <!-- 에러 텍스트 -->
        <div id="${DOM_IDS.ERROR_TEXT}" style="
          margin-top: 10px;
          text-align: center;
          color: #ff4444;
          font-size: 14px;
          min-height: 20px;
        "></div>
      </div>
    `;
  }

  /**
   * ConnectionManager 초기화 및 Gateway 연결
   */
  private initializeConnection(): void {
    console.log('[LoginScene] Gateway URL:', ServerConfig.GATEWAY_URL);
    console.log('[LoginScene] Window location:', window.location.host);

    this.connectionManager = ConnectionManager.getInstance(ServerConfig.GATEWAY_URL);

    // 리스너 등록은 연결 여부와 무관하게 항상 수행한다.
    // (이미 연결된 상태로 씬에 재진입해도 login_result 등을 받을 수 있어야 한다)
    this.registerConnectionListeners();

    // 이미 연결된 경우 — 연결 절차는 생략하고 자동 로그인만 시도
    if (this.connectionManager.isConnected()) {
      console.log('[LoginScene] Already connected to Gateway');
      this.updateStatus(LoginStatus.IDLE, 'Connected to Gateway');
      this.tryAutoLogin();
      return;
    }

    console.log('[LoginScene] Connecting to Gateway...');
    this.updateStatus(LoginStatus.CONNECTING, 'Connecting to Gateway...');

    // 자동으로 연결 시작
    console.log('[LoginScene] Calling connect() automatically...');
    this.connectionManager
      .connect()
      .then(() => {
        console.log('[LoginScene] Auto-connect successful');
      })
      .catch((err: Error) => {
        console.error('[LoginScene] Auto-connect failed:', err);
        // 자동 연결 실패 시에도 자동 로그인 시도는 진행 (재연결 시도 위함)
        this.updateStatus(LoginStatus.ERROR, '', `Connection failed: ${err.message}`);
        this.currentStatus = LoginStatus.ERROR;
      });
  }

  /**
   * ConnectionManager 구독 + shutdown 시 해제를 위한 참조 추적 헬퍼.
   */
  private onNet(event: string, handler: (data: unknown) => void): void {
    this.connectionManager?.on(event, handler);
    this.connectionListeners.push({ event, handler });
  }

  /**
   * ConnectionManager 이벤트 리스너 등록 (연결 로직과 분리).
   * 등록된 핸들러 참조는 connectionListeners에 보관되어 shutdown 시 해제된다.
   */
  private registerConnectionListeners(): void {
    // 연결 이벤트 리스너
    this.onNet('connected', () => {
      console.log('[LoginScene] Connected to Gateway!');
      // 중복 로그인 "확인" 이후 재접속을 기다리던 중이라면, 자동 로그인 대신
      // 보류해 둔 force 로그인을 이어서 보낸다 (사용자 개입 없이 완결).
      if (this.forceLoginPending) {
        this.sendForceLogin();
        return;
      }
      this.updateStatus(LoginStatus.IDLE, 'Connected to Gateway');
      // 새로 연결되면 저장된 자격증명이 있는지 확인하여 자동 로그인 진행
      this.tryAutoLogin();
    });

    // 연결 끊김 이벤트
    this.onNet('disconnected', (data: unknown) => {
      console.log('[LoginScene] Disconnected:', data);
      const reason = (data as { reason?: string })?.reason || 'Unknown';
      // 기존 세션을 실제로 끊는 주체는 게임 서버이고, 그 여파로 게이트웨이↔게임서버
      // TCP는 물론 브라우저 WebSocket까지 닫힐 수 있다. 강제 로그인 진행 중이라면
      // 이 종료는 실패가 아니라 예상된 경유지이므로 재접속 → 재로그인으로 이어간다.
      if (this.forceLoginPending) {
        this.retryForceLogin(`연결 종료(${reason})`);
        return;
      }
      this.updateStatus(LoginStatus.ERROR, '', `Disconnected: ${reason}`);
      this.currentStatus = LoginStatus.ERROR;
    });

    // 재연결 실패
    this.onNet('reconnect_failed', () => {
      this.updateStatus(LoginStatus.ERROR, '', 'Failed to reconnect to Gateway');
      this.currentStatus = LoginStatus.ERROR;
    });

    // 에러 이벤트
    this.onNet('error', (data: unknown) => {
      console.error('[LoginScene] Connection error:', data);
      const message = LoginScene.extractErrorMessage(data);
      // 강제 로그인 중의 오류는 대부분 기존 세션 정리와 겹쳐 게임 서버가 새 접속까지
      // 끊어버린 경우다(게이트웨이가 "Disconnected from game server"를 보낸다).
      // 사용자에게 되묻지 말고 1회 한도로 다시 시도한다.
      if (this.forceLoginPending) {
        this.retryForceLogin(message);
        return;
      }
      this.updateStatus(LoginStatus.ERROR, '', `Connection error: ${message}`);
      this.currentStatus = LoginStatus.ERROR;
    });

    // 로그인 결과 처리
    this.onNet('login_result', (data: unknown) => {
      this.handleLoginResult(data as LoginResultPayload);
    });

    // 중복 로그인 감지 — 게이트웨이가 사용자 확인을 요청
    this.onNet('duplicate_login', (data: unknown) => {
      const message = (data as { message?: string })?.message
        || '이 계정은 이미 접속 중입니다. 기존 세션을 종료하고 접속하시겠습니까?';
      // 이미 "확인"을 눌러 강제 로그인이 진행 중인데 또 물어온다면 기존 세션이
      // 아직 정리되지 않은 것이다. 다이얼로그를 다시 띄우지 말고 재시도 한도
      // 안에서 자동으로 한 번 더 보낸다. 한도를 넘겼으면 사용자에게 되묻는다.
      if (this.forceLoginPending && !this.forceLoginRetried) {
        this.retryForceLogin('기존 세션이 아직 종료되지 않음');
        return;
      }
      this.clearForceLoginState();
      this.showDuplicateLoginModal(message);
    });
  }

  /**
   * error 이벤트 페이로드에서 표시할 메시지를 뽑는다.
   * 게이트웨이의 error 메시지는 `{ message }`, WebSocket onerror는 Event/Error로 온다.
   */
  private static extractErrorMessage(data: unknown): string {
    return (data as { message?: string } | null)?.message || 'Unknown';
  }

  /**
   * 로그인 제출 처리
   */
  private handleLoginSubmit(
    accountInput: HTMLInputElement,
    passwordInput: HTMLInputElement,
    statusText: HTMLElement,
    errorText: HTMLElement
  ): void {
    const account = accountInput.value.trim();
    const password = passwordInput.value;

    // 사용자가 직접 로그인 버튼/Enter로 시도한 것이므로 자동 로그인 플래그 해제
    this.isAutoLoginAttempt = false;
    // 사용자가 새로 시도했으므로 진행 중이던 강제 재로그인 흐름은 접는다
    this.clearForceLoginState();

    console.log('[LoginScene] Login attempt:', account);

    // 입력 검증
    if (!account) {
      errorText.textContent = 'Please enter your account';
      accountInput.focus();
      return;
    }

    if (!password) {
      errorText.textContent = 'Please enter your password';
      passwordInput.focus();
      return;
    }

    // 연결 상태 확인
    if (!this.connectionManager?.isConnected()) {
      console.log('[LoginScene] Not connected, connecting first...');
      this.updateStatus(LoginStatus.CONNECTING, 'Connecting to Gateway...');
      this.connectAndLogin(account, password);
      return;
    }

    // 로그인 요청 전송
    this.performLogin(account, password);
  }

  /**
   * Gateway에 연결 후 로그인 수행
   */
  private connectAndLogin(account: string, password: string): void {
    if (!this.connectionManager) {
      console.error('[LoginScene] No connectionManager');
      return;
    }

    console.log('[LoginScene] Calling connect()...');
    this.connectionManager
      .connect()
      .then(() => {
        console.log('[LoginScene] Connect successful, performing login...');
        this.performLogin(account, password);
      })
      .catch((err: Error) => {
        console.error('[LoginScene] Connect failed:', err);
        this.updateStatus(LoginStatus.ERROR, '', `Connection failed: ${err.message}`);
      });
  }

  /**
   * 로그인 요청 전송
   */
  private performLogin(account: string, password: string): void {
    console.log('[LoginScene] Sending login request:', { account });
    if (!this.connectionManager) return;

    // 중복 로그인 확인 다이얼로그에서 "확인"을 누르면 force:true로 재전송할 때 쓴다.
    this.pendingCredentials = { account, password };

    this.updateStatus(LoginStatus.LOGGING_IN, 'Logging in...');
    this.setFormEnabled(false);

    try {
      this.connectionManager.send({
        type: 'login',
        payload: { account, password },
      });
    } catch (err) {
      const error = err as Error;
      this.updateStatus(LoginStatus.ERROR, '', `Send failed: ${error.message}`);
      this.setFormEnabled(true);
    }
  }

  /**
   * 로그인 결과 처리
   */
  private handleLoginResult(payload: LoginResultPayload): void {
    // 결과가 왔다는 것은 강제 재로그인 흐름이 끝났다는 뜻 — 타임아웃/재시도 타이머 해제
    this.clearForceLoginState();

    if (payload.result === 0) {
      // 로그인 성공 — 마지막 입력값을 보관하여 새로 고침 후 자동 로그인에 사용
      const accountInput = this.domContainer?.getChildByID(DOM_IDS.ACCOUNT_INPUT) as HTMLInputElement | null;
      const passwordInput = this.domContainer?.getChildByID(DOM_IDS.PASSWORD_INPUT) as HTMLInputElement | null;

      if (accountInput && passwordInput) {
        const account = accountInput.value.trim();
        const password = passwordInput.value;
        if (account && password) {
          storageManager.saveCredentials(account, password, SessionState.AUTHENTICATED, null);
          console.log('[LoginScene] 자격증명 저장 완료 (자동 로그인용):', account);
        }
      }

      this.isAutoLoginAttempt = false;
      this.updateStatus(LoginStatus.SUCCESS, 'Login successful!');
      this.currentStatus = LoginStatus.SUCCESS;

      // 잠시 후 캐릭터 선택 화면으로 전환
      this.time.delayedCall(500, () => {
        this.scene.start('CharSelectScene');
      });
    } else {
      // 로그인 실패 — 자동 로그인으로 시도한 자격증명이 유효하지 않으므로 삭제.
      // (currentStatus는 performLogin()이 LOGGING_IN으로 덮어쓰기 때문에
      //  isAutoLoginAttempt 필드로 자동 로그인 여부를 판별한다)
      if (this.isAutoLoginAttempt) {
        storageManager.clearCredentials();
        console.warn('[LoginScene] 자동 로그인 실패 — 저장된 자격증명 삭제');
        this.isAutoLoginAttempt = false;
      }

      // 실패 메시지를 사용자 입력칸 위로 표시
      this.updateStatus(LoginStatus.ERROR, '', payload.message || 'Login failed');
      this.currentStatus = LoginStatus.ERROR;
      this.setFormEnabled(true);
    }
  }

  /**
   * 중복 로그인 확인 모달 표시.
   * 게이트웨이가 같은 계정으로 이미 접속 중인 세션을 감지했을 때 온다.
   * 확인 시 force:true로 로그인을 재전송해 기존 세션을 종료하고 접속한다.
   */
  private showDuplicateLoginModal(message: string): void {
    if (this.duplicateLoginModal) return; // 이미 떠 있으면 중복 표시하지 않음

    this.setFormEnabled(false);
    this.updateStatus(LoginStatus.IDLE, '');

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // 반투명 배경 딤 처리는 별도 Phaser Graphics로 그린다 — DOM 요소는 Phaser가
    // transform으로 위치를 잡는 컨테이너 안에 렌더링되는데, 그 조상에 transform이
    // 걸려 있으면 CSS 스펙상 자식의 position:fixed가 뷰포트가 아니라 그
    // transform된 조상 기준으로 동작해버려(전형적인 CSS 함정) "화면 전체를 덮는
    // 오버레이"가 의도한 크기로 나오지 않는다. 로그인 폼과 동일하게 DOM 요소는
    // 카드 UI 자체만 담당하고, 배경 딤은 일반 게임 오브젝트로 분리한다.
    this.duplicateLoginBackdrop = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.65);
    this.duplicateLoginBackdrop.setInteractive(); // 뒤쪽 폼 클릭 차단
    this.duplicateLoginBackdrop.setDepth(999);

    const html = `
      <div style="
        background: linear-gradient(180deg, rgba(20, 20, 35, 0.98) 0%, rgba(10, 10, 18, 0.99) 100%);
        border: 2px solid #c9a227;
        border-radius: 8px;
        padding: 28px 32px;
        width: 300px;
        box-sizing: border-box;
        text-align: center;
        font-family: Arial, sans-serif;
        box-shadow: 0 0 30px rgba(201, 162, 39, 0.3);
      ">
        <div id="${DUP_MODAL_IDS.MESSAGE}" style="
          color: #ffcc44;
          font-size: 15px;
          line-height: 1.5;
          margin-bottom: 22px;
          white-space: pre-line;
        "></div>
        <div style="display: flex; gap: 10px; justify-content: center;">
          <button id="${DUP_MODAL_IDS.CANCEL_BTN}" type="button" style="
            flex: 1; padding: 10px 0;
            background: #333333; border: none; border-radius: 4px;
            color: #cccccc; font-size: 14px; cursor: pointer;
          ">취소</button>
          <button id="${DUP_MODAL_IDS.CONFIRM_BTN}" type="button" style="
            flex: 1; padding: 10px 0;
            background: linear-gradient(180deg, #c9a227 0%, #8b7019 100%);
            border: none; border-radius: 4px;
            color: #000000; font-weight: bold; font-size: 14px; cursor: pointer;
          ">확인</button>
        </div>
      </div>
    `;

    this.duplicateLoginModal = this.add.dom(width / 2, height / 2).createFromHTML(html);
    this.duplicateLoginModal.setDepth(1000);

    // innerHTML 보간 대신 textContent로 채워 메시지 내 HTML이 해석되지 않도록 한다.
    const messageEl = this.duplicateLoginModal.getChildByID(DUP_MODAL_IDS.MESSAGE) as HTMLElement | null;
    if (messageEl) messageEl.textContent = message;

    const confirmBtn = this.duplicateLoginModal.getChildByID(DUP_MODAL_IDS.CONFIRM_BTN) as HTMLButtonElement | null;
    const cancelBtn = this.duplicateLoginModal.getChildByID(DUP_MODAL_IDS.CANCEL_BTN) as HTMLButtonElement | null;

    confirmBtn?.addEventListener('click', () => {
      this.closeDuplicateLoginModal();
      this.beginForceLogin();
    });

    cancelBtn?.addEventListener('click', () => {
      this.closeDuplicateLoginModal();
      this.updateStatus(LoginStatus.IDLE, '', '로그인이 취소되었습니다.');
      this.setFormEnabled(true);
    });
  }

  /**
   * 중복 로그인 다이얼로그의 "확인" 처리 — 기존 세션을 끊고 이 세션으로 접속한다.
   *
   * 기존 접속을 실제로 끊는 주체는 게임 서버다(`LoginController.login()`이 기존
   * ClientThread를 kick하고 새 접속에도 AccountAlreadyLoginException을 던진다).
   * 그래서 force 로그인을 보낸 직후, 아직 정리되지 않은 기존 접속과 겹쳐 **새
   * 접속까지 끊기는** 경우가 있고, 그때 게이트웨이는 "Disconnected from game
   * server" 오류만 보내고 끝난다. 예전에는 여기서 흐름이 죽어 사용자가 LOGIN을
   * 한 번 더 눌러야 했다. 이제 아래 흐름이 그 한 번을 대신한다:
   *
   *  - 오류/연결 종료를 받으면 재접속 후 force 로그인을 **1회만** 자동 재전송
   *  - 재전송까지 실패하면 한국어 에러를 남기고 폼을 되살린다
   *  - 전체 흐름은 FORCE_LOGIN.TIMEOUT_MS 안에 끝나야 하며, 넘기면 로딩을 푼다
   */
  private beginForceLogin(): void {
    if (!this.pendingCredentials || !this.connectionManager) {
      this.updateStatus(LoginStatus.ERROR, '', '로그인 정보가 없습니다. 다시 로그인해 주세요.');
      this.setFormEnabled(true);
      return;
    }

    this.clearForceLoginTimers();
    this.forceLoginPending = true;
    this.forceLoginRetried = false;

    // 전체 제한 시간 — 재접속/재로그인이 끝내 완료되지 않아도 로딩에 갇히지 않는다.
    this.forceLoginTimeoutEvent = this.time.delayedCall(FORCE_LOGIN.TIMEOUT_MS, () => {
      this.forceLoginTimeoutEvent = null;
      this.failForceLogin('기존 세션을 종료하고 접속하지 못했습니다. 잠시 후 다시 로그인해 주세요.');
    });

    this.sendForceLogin();
  }

  /**
   * force:true 로그인 전송. 연결이 끊겨 있으면 먼저 재연결하고, 실제 전송은
   * 'connected' 이벤트를 받은 뒤에 한다 — 여기서 곧바로 send()하면
   * ConnectionManager가 큐에 쌓았다가 재연결 시 flush하면서 이중 전송이 된다.
   */
  private sendForceLogin(): void {
    if (!this.forceLoginPending) return;

    const credentials = this.pendingCredentials;
    if (!credentials || !this.connectionManager) {
      this.failForceLogin('로그인 정보가 없습니다. 다시 로그인해 주세요.');
      return;
    }

    if (!this.connectionManager.isConnected()) {
      this.updateStatus(LoginStatus.CONNECTING, '게이트웨이에 다시 연결하는 중...');
      this.setFormEnabled(false);
      this.connectionManager.connect().catch((err: Error) => {
        this.retryForceLogin(`재연결 실패: ${err.message}`);
      });
      return;
    }

    this.updateStatus(LoginStatus.LOGGING_IN, '기존 세션을 종료하고 접속 중...');
    this.setFormEnabled(false);

    try {
      this.connectionManager.send({
        type: 'login',
        payload: { ...credentials, force: true },
      });
    } catch (err) {
      this.retryForceLogin(`전송 실패: ${(err as Error).message}`);
    }
  }

  /**
   * 강제 로그인이 실패했을 때 1회에 한해 재시도를 예약한다.
   * 게임 서버가 기존 접속을 정리하는 데 시간이 걸리므로 곧바로 다시 보내지 않는다.
   * (연결 종료 → 오류 처럼 이벤트가 겹쳐 와도 예약은 하나만 걸린다)
   */
  private retryForceLogin(reason: string): void {
    if (!this.forceLoginPending) return;
    if (this.forceLoginRetryEvent) return;

    console.warn('[LoginScene] 강제 로그인 실패:', reason);

    if (this.forceLoginRetried) {
      this.failForceLogin(`기존 세션을 종료하고 접속하지 못했습니다 (${reason}). 다시 로그인해 주세요.`);
      return;
    }

    this.forceLoginRetried = true;
    this.updateStatus(LoginStatus.CONNECTING, '기존 세션 종료를 기다리는 중...');
    this.setFormEnabled(false);
    this.forceLoginRetryEvent = this.time.delayedCall(FORCE_LOGIN.RETRY_DELAY_MS, () => {
      this.forceLoginRetryEvent = null;
      this.sendForceLogin();
    });
  }

  /** 강제 로그인 포기 — 로딩을 풀고 한국어 에러를 남긴다. */
  private failForceLogin(message: string): void {
    this.clearForceLoginState();
    this.updateStatus(LoginStatus.ERROR, '', message);
    this.currentStatus = LoginStatus.ERROR;
    this.setFormEnabled(true);
  }

  /** 강제 로그인 진행 상태와 타이머를 모두 해제 */
  private clearForceLoginState(): void {
    this.forceLoginPending = false;
    this.forceLoginRetried = false;
    this.clearForceLoginTimers();
  }

  private clearForceLoginTimers(): void {
    this.forceLoginTimeoutEvent?.remove(false);
    this.forceLoginTimeoutEvent = null;
    this.forceLoginRetryEvent?.remove(false);
    this.forceLoginRetryEvent = null;
  }

  private closeDuplicateLoginModal(): void {
    this.duplicateLoginModal?.destroy();
    this.duplicateLoginModal = null;
    this.duplicateLoginBackdrop?.destroy();
    this.duplicateLoginBackdrop = null;
  }

  /**
   * 상태 텍스트 업데이트
   */
  private updateStatus(status: LoginStatus, message: string, error: string = ''): void {
    this.currentStatus = status;

    // 인트로가 화면을 덮고 있으면 폼의 오류 문구는 아무도 볼 수 없다.
    // 스스로 회복할 수 없는 상태이므로 인트로 쪽에 옮겨 알린다.
    if (status === LoginStatus.ERROR && isIntroRoute()) {
      introOverlay.showError(error || message || '서버에 연결하지 못했습니다.');
    }

    if (!this.domContainer) return;

    const statusText = this.domContainer.getChildByID(DOM_IDS.STATUS_TEXT) as HTMLElement;
    const errorText = this.domContainer.getChildByID(DOM_IDS.ERROR_TEXT) as HTMLElement;
    const submitBtn = this.domContainer.getChildByID(DOM_IDS.SUBMIT_BTN) as HTMLButtonElement;

    if (statusText) {
      // 상태에 따른 색상
      let color = '#888888';
      if (
        status === LoginStatus.CONNECTING ||
        status === LoginStatus.LOGGING_IN ||
        status === LoginStatus.AUTO_LOGGING_IN
      ) {
        color = '#c9a227'; // 골드
      } else if (status === LoginStatus.SUCCESS) {
        color = '#44ff44'; // 그린
      } else if (status === LoginStatus.ERROR) {
        color = '#ff4444'; // 레드
      }

      // innerHTML 보간 대신 textContent + style.color 사용 (HTML 주입 방지)
      statusText.style.color = color;
      statusText.textContent = message || '';
    }

    if (errorText) {
      // 로그인 진행 중에는 마지막 캐릭터 안내 메시지를 유지하기 위해 message가 있으면 덮어쓰지 않는다.
      if (!error || status === LoginStatus.AUTO_LOGGING_IN) {
        if (!message || status !== LoginStatus.AUTO_LOGGING_IN) {
          errorText.textContent = error || '';
        }
      } else {
        errorText.textContent = error;
      }
    }

    // 로딩 중 버튼 비활성화
    if (submitBtn) {
      const isLoading =
        status === LoginStatus.CONNECTING ||
        status === LoginStatus.LOGGING_IN ||
        status === LoginStatus.AUTO_LOGGING_IN;
      submitBtn.innerHTML = isLoading ? 'Please wait...' : 'Login';
      (submitBtn as HTMLButtonElement).disabled = isLoading;
    }
  }

  /**
   * 폼 활성화/비활성화
   */
  private setFormEnabled(enabled: boolean): void {
    if (!this.domContainer) return;

    const accountInput = this.domContainer.getChildByID(DOM_IDS.ACCOUNT_INPUT) as HTMLInputElement;
    const passwordInput = this.domContainer.getChildByID(DOM_IDS.PASSWORD_INPUT) as HTMLInputElement;
    const submitBtn = this.domContainer.getChildByID(DOM_IDS.SUBMIT_BTN) as HTMLButtonElement;

    if (accountInput) accountInput.disabled = !enabled;
    if (passwordInput) passwordInput.disabled = !enabled;
    if (submitBtn) (submitBtn as HTMLButtonElement).disabled = !enabled;
  }

  /**
   * 씬 종료(shutdown 이벤트) 시 정리.
   * ConnectionManager는 싱글톤으로 씬보다 오래 살아남으므로, 이 씬이 등록한
   * 리스너를 등록 시 보관해둔 동일 참조로 off()하지 않으면 씬 재진입마다
   * 핸들러가 중복 등록된다.
   */
  private cleanup(): void {
    if (this.connectionManager) {
      this.connectionListeners.forEach(({ event, handler }) => {
        this.connectionManager?.off(event, handler);
      });
    }
    this.connectionListeners = [];

    // 강제 재로그인 타이머는 씬보다 오래 살면 안 된다 (씬 파괴 후 콜백 방지)
    this.clearForceLoginState();

    this.domContainer?.destroy();
    this.domContainer = null;

    this.duplicateLoginModal?.destroy();
    this.duplicateLoginModal = null;
    this.duplicateLoginBackdrop?.destroy();
    this.duplicateLoginBackdrop = null;
  }
}
